import { useCallback } from "react";

import {
  useInvalidateEveryOperation,
  useClearEveryOperation,
} from "./operation";
import {
  useInvalidatePreOperations,
  useClearPreOperations,
} from "./preOperations";

// #############################
// ## useInvalidateOperations ##
// #############################

export type ResultOfUseInvalidateOperations = () => Promise<void>;
export type UseInvalidateOperations = () => ResultOfUseInvalidateOperations;

/**
 * Invalidates the operations data.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useInvalidateOperations: UseInvalidateOperations = () => {
  const invalidateEveryOperation = useInvalidateEveryOperation();
  const invalidatePreOperations = useInvalidatePreOperations();

  return useCallback(async () => {
    await Promise.all([invalidatePreOperations(), invalidateEveryOperation()]);
  }, [invalidateEveryOperation, invalidatePreOperations]);
};

// ########################
// ## useClearOperations ##
// ########################

export type ResultOfUseClearOperations = () => Promise<void>;
export type UseClearOperations = () => ResultOfUseClearOperations;

/**
 * Clears and invalidates the operations data.
 *
 * A fresh fetch of data will be initiated when a hook instance is active that uses this data.
 */
export const useClearOperations: UseClearOperations = () => {
  const clearEveryOperation = useClearEveryOperation();
  const clearPreOperations = useClearPreOperations();

  return useCallback(async () => {
    // IMPORTANT: Order here matters!! clearPreOperations must be started before clearEveryOperation
    await Promise.all([clearPreOperations(), clearEveryOperation()]);
  }, [clearEveryOperation, clearPreOperations]);
};
